/**
 * Reopening the guided setup for the project the mapping workspace is showing.
 *
 * It is deliberately separate from the navigation guard: the guard decides
 * whether the user may leave, this decides how setup is reopened once they may.
 */

import { useCallback } from 'react';
import { canOpenSetupRevision } from '../../../shared/lib/setupRevision';
import { normalizeMappingProjectKey } from '../../../shared/lib/mappingProjectIdentity';
import { useSetupRevisionStatus, useUIStore } from '../../../shared/stores/useUIStore';
import type { Screen } from '../../../shared/types';

export interface SetupRevisionReopen {
  /** Whether leaving for setup can open a revision for the active project. */
  canReopen: boolean;
  /** Opens the revision at `boundaryScreen`; a no-op without an active project. */
  reopen: (boundaryScreen: Screen) => void;
}

export function useSetupRevisionReopen(activeProjectKey: string | null): SetupRevisionReopen {
  const status = useSetupRevisionStatus();
  const revisionProjectKey = useUIStore((state) => state.setupRevisionProjectKey);
  const beginSetupRevision = useUIStore((state) => state.beginSetupRevision);
  const projectKey = normalizeMappingProjectKey(activeProjectKey);

  const reopen = useCallback(
    (boundaryScreen: Screen) => {
      if (!projectKey) {
        return;
      }

      beginSetupRevision({ projectKey, boundaryScreen });
    },
    [beginSetupRevision, projectKey]
  );

  return {
    canReopen: projectKey !== null && canOpenSetupRevision(status, revisionProjectKey, projectKey),
    reopen,
  };
}
